import { useDispatch, useSelector } from 'react-redux';
import { getMediaQueryDetails, getPortalQueryDetails, getPublishedPortalDetails, getForm } from '../../../actions/publishActions';
import { showMessage } from '../../../actions/globalActions';
import { useTranslation } from 'react-i18next';
import { mediaNames, mediaTypes, messageTypes } from '../../../constants/mediaConstants';
import { getDetails } from '../../../services/mediaDisplayService';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getAccountSettings } from '../../../actions/profileActions';

interface FetchMediaProps {
	mediaId: string;
	mediaName?: string;
	mediaType?: string;
	refreshData: boolean;
	errorMessage?: string;
	resetRefreshData?: () => void;
}
interface FetchMediaResult {
	mediaDetails: any | undefined;
	embedVersion: string;
	publishPortalDetails?: any;
}

const useFetchMediaDetails = ({
	mediaId,
	mediaName,
	mediaType,
	refreshData,
	errorMessage,
	resetRefreshData,
}: FetchMediaProps): FetchMediaResult => {
	const dispatch = useDispatch<any>();
	const { t } = useTranslation();

	const { defaultAccountId } = useSelector((state: any) => state.session);

	const [mediaDetails, setMediaDetails] = useState<any | undefined>(undefined);
	const [publishPortalDetails, setPublishPortalDetails] = useState<any | undefined>(undefined);
	const [embedVersion, setEmbedVersion] = useState('1');

	const requestIdRef = useRef(0);

	const fetchMedia = useCallback(() => {
		const id = ++requestIdRef.current;
		if (!mediaId) {
			mediaDetails && setMediaDetails(undefined);
			return;
		}

		if (mediaType === mediaTypes.portal) {
			// process for portal

			dispatch(getPortalQueryDetails(defaultAccountId, mediaId)).then((portal: any) => {
				if (!portal) {
					dispatch(showMessage(errorMessage ?? t('COMMON_MEDIA_NOT_FOUND'), messageTypes.error));
					mediaDetails && setMediaDetails(undefined);
					return;
				}
				if (typeof portal == 'string') {
					dispatch(showMessage(portal, messageTypes.error));
					mediaDetails && setMediaDetails(undefined);
					return;
				}

				if (portal.code) {
					dispatch(getPublishedPortalDetails(portal.code)).then((portalDetails: any) => {
						setPublishPortalDetails(portalDetails);
					});
				}

				setMediaDetails(portal);
			});

			return;
		}

		if (mediaType === mediaTypes.form) {
			// process for form

			dispatch(getForm(defaultAccountId, mediaId)).then((form: any) => {
				if (!form) {
					dispatch(showMessage(errorMessage ?? t('COMMON_FORM_NOT_FOUND'), messageTypes.error));
					mediaDetails && setMediaDetails(undefined);
					return;
				}
				if (typeof form === 'string') {
					dispatch(showMessage(form, messageTypes.error));
					mediaDetails && setMediaDetails(undefined);
					return;
				}

				// makes sure that if the getForm request is too slow to resolve,
				// it won't overwrite mediaDetails if the component re-renders
				if (id === requestIdRef.current) {
					setMediaDetails(form);
				}
			});

			return;
		}

		dispatch(getMediaQueryDetails(defaultAccountId, mediaId, mediaName)).then((media: any) => {
			if (!media) {
				dispatch(showMessage(errorMessage ?? t('COMMON_MEDIA_NOT_FOUND'), messageTypes.error));
				mediaDetails && setMediaDetails(undefined);
				return;
			}
			if (typeof media == 'string') {
				dispatch(showMessage(media, messageTypes.error));
				mediaDetails && setMediaDetails(undefined);
				return;
			}

			const mediaInfo = getDetails(media, mediaName === mediaNames.playlists);
			setMediaDetails(mediaInfo);
		});
	}, [defaultAccountId, dispatch, errorMessage, mediaDetails, mediaId, mediaName, t, mediaType]);

	const loadEmbedSetting = useCallback(() => {
		dispatch(getAccountSettings(defaultAccountId)).then((settings: any) => {
			if (!(settings && Object.keys(settings).length > 0)) {
				return;
			}

			let embed = { version: '1' };
			if (
				settings.hasOwnProperty('qbrickPlayer') &&
				settings['qbrickPlayer'] &&
				settings['qbrickPlayer'].length > 0
			) {
				embed = settings['qbrickPlayer'].find((v: any) => !!v.enabled);
			}

			setEmbedVersion((prevEmbedVersion) =>
				embed && prevEmbedVersion !== embed.version ? embed.version : prevEmbedVersion
			);
		});
	}, [defaultAccountId, dispatch]);

	useEffect(() => {
		if (!refreshData) {
			return;
		}
		fetchMedia();
		loadEmbedSetting();
		resetRefreshData?.();
	}, [fetchMedia, loadEmbedSetting, refreshData, resetRefreshData]);

	return { mediaDetails, embedVersion, publishPortalDetails };
};

export default useFetchMediaDetails;
